import { Component, OnInit, ViewChild, ChangeDetectorRef } from "@angular/core";
import { Router } from "@angular/router";
import {
  ApexAxisChartSeries,
  ApexChart,
  ApexDataLabels,
  ApexFill,
  ApexLegend,
  ApexNonAxisChartSeries,
  ApexPlotOptions,
  ApexResponsive,
  ApexTooltip,
  ApexXAxis,
  ApexYAxis,
  ChartComponent,
} from "ng-apexcharts";
import { BaseComponent } from "src/app/shared/components/base/base.component";
import { AccountService } from "../../../users/data/services/account.service";
import { GivingService } from "../../../users/data/services/giving.service";
import { TokenStorageService } from "src/app/core/service/token-storage.service";
import { takeUntil } from "rxjs";

export type ChartOptions = {
  series?: ApexAxisChartSeries;
  radialseries?: ApexNonAxisChartSeries;
  chart?: ApexChart;
  dataLabels?: ApexDataLabels;
  plotOptions?: ApexPlotOptions;
  yaxis?: ApexYAxis;
  xaxis?: ApexXAxis;
  fill?: ApexFill;
  tooltip?: ApexTooltip;
  legend?: ApexLegend;
  colors?: string[];
  responsive?: ApexResponsive[];
};

@Component({
  selector: "app-dashboard",
  templateUrl: "./dashboard.component.html",
  styleUrls: ["./dashboard.component.sass"],
})
export class DashboardComponent extends BaseComponent implements OnInit {
  @ViewChild("chart") chart: ChartComponent;

  public barChartOptions: Partial<ChartOptions> = {
    series: [{ name: "Members Joined", data: [] }],
    chart: { type: "bar", height: 350, foreColor: "#6f6f6f" },
    colors: ["#b32317"],
    plotOptions: {
      bar: { horizontal: false, columnWidth: "75%", borderRadius: 6 },
    },
    xaxis: { categories: [], labels: { style: { colors: "#6f6f6f" } } },
    fill: { opacity: 1 },
    legend: { show: false },
    tooltip: { theme: "light", marker: { show: true }, x: { show: true } },
    dataLabels: { enabled: false },
  };
  public radialChartOptions: Partial<ChartOptions> = {
    radialseries: [0, 0, 0, 0],
    chart: { height: 300, type: "donut" },
    colors: ["#B32317", "#49A942", "#8F1C12", "#221F20"],
    plotOptions: {
      pie: {
        donut: {
          size: "40%",
          labels: {
            show: true,
            name: { show: true, fontSize: "16px", fontWeight: 600 },
            value: {
              show: true,
              fontSize: "18px",
              fontWeight: 700,
              formatter: (val: any) => {
                const num = Number(val) || 0;
                return Math.round(num).toLocaleString();
              },
            },
            total: {
              show: true,
              label: "Total Amount",
              fontSize: "14px",
              fontWeight: "bold",
              color: "#221F20",
              formatter: () => "0",
            },
          },
        },
      },
    },
    tooltip: {
      enabled: true,
      y: {
        formatter: (val: number, { seriesIndex }: any) => {
          const labels = ["Offertory", "Project Fund", "Tithe", "Donations"];
          return `${labels[seriesIndex]}: KES ${new Intl.NumberFormat().format(val)}`;
        },
      },
    },
    dataLabels: { enabled: false },
    legend: { show: false },
  };

  members: any[] = [];
  parishId: any;
  membersCountPerMonth: number[] = [];
  membersCount = 0;

  // Giving totals for donut chart
  givingTotals = {
    offertory: 0,
    projectFund: 0,
    tithe: 0,
    donations: 0,
  };

  constructor(
    private accountService: AccountService,
    private givingService: GivingService,
    private router: Router,
    private tokenStorage: TokenStorageService,
    private cdr: ChangeDetectorRef,
  ) {
    super();
  }

  ngOnInit(): void {
    this.parishId = this.tokenStorage.getUser().parishId;
    this.membersData();
    this.outStations();
    this.fetchGivingTotals();
    this.members = JSON.parse(localStorage.getItem("family")) || [];
  }

  private membersData(): void {
    this.accountService
      .members(this.parishId)
      .pipe(takeUntil(this.subject))
      .subscribe(
        (res) => {
          this.membersCount = res.length;
          this.calculateMembersCountPerMonth(res);
        },
        (err) => console.error(err),
      );
  }

  private calculateMembersCountPerMonth(data: any[]): void {
    this.membersCountPerMonth = Array(12).fill(0);
    const currentYear = new Date().getFullYear();
    data.forEach((member) => {
      const enrollmentDate = new Date(member.postedTime);
      if (enrollmentDate.getFullYear() === currentYear) {
        const month = enrollmentDate.getMonth();
        this.membersCountPerMonth[month]++;
      }
    });
  }

  private outStations(): void {
    this.accountService.getoutStationDetails(this.parishId).subscribe(
      (res) => {
        const membersData = res.map((d) => d.members);
        const categories = res.map((d) => d.name);
        this.initBarChart(membersData, categories);
      },
      (err) => console.error(err),
    );
  }

  private initBarChart(membersData: number[], categories: string[]): void {
    this.barChartOptions = {
      series: [{ name: "Members Joined", data: membersData }],
      chart: { type: "bar", height: 350, foreColor: "#6f6f6f" },
      colors: ["#b32317"],
      plotOptions: {
        bar: { horizontal: false, columnWidth: "75%", borderRadius: 6 },
      },
      xaxis: { categories, labels: { style: { colors: "#6f6f6f" } } },
      fill: { opacity: 1 },
      legend: { show: false },
      tooltip: { theme: "light", marker: { show: true }, x: { show: true } },
      dataLabels: { enabled: false },
    };
  }

  private fetchGivingTotals(): void {
    const user = this.tokenStorage.getUser();
    const churchUnitId = user?.churchUnitId || this.parishId;

    // Offertory
    this.givingService.getTotalOffertories(this.parishId).subscribe({
      next: (res) => {
      this.givingTotals.offertory = res?.entity?.overallTotal ?? 0;
      this.updateDonutChart();
      },
      error: (err) => {
        console.error("Offertory fetch failed:", err);
        this.givingTotals.offertory = 0;
        this.updateDonutChart();
      }
    });

    // Project Funds (PDF)
    this.givingService.getTotalPdfs(churchUnitId).subscribe({
      next: (res) => {
      this.givingTotals.projectFund = res ?? 0;
      this.updateDonutChart();
      },
      error: (err) => {
        console.error("Project Fund fetch failed:", err);
        this.givingTotals.projectFund = 0;
        this.updateDonutChart();
      }
    });

    // Tithe
    this.givingService.getTotalTithes(churchUnitId).subscribe({
      next: (res) => {
      this.givingTotals.tithe = res?.entity ?? 0;
      this.updateDonutChart();
      },
      error: (err) => {
        console.error("Tithe fetch failed:", err);
        this.givingTotals.tithe = 0;
        this.updateDonutChart();
      }
    });

    // Donations
    this.givingService.getAllDonation(churchUnitId).subscribe({
      next: (res) => {
        this.givingTotals.donations = res?.entity ?? 0;
        this.updateDonutChart();
      },
      error: (err) => {
        console.error("Donations fetch failed:", err);
        this.givingTotals.donations = 0;
        this.updateDonutChart();
      },
    });
  }

  private updateDonutChart(): void {
    const { offertory, projectFund, tithe, donations } = this.givingTotals;
    const total = offertory + projectFund + tithe + donations;

    this.radialChartOptions = {
      radialseries: [offertory, projectFund, tithe, donations],
      chart: { height: 300, type: "donut" },
      colors: ["#B32317", "#49A942", "#8F1C12", "#221F20"],
      plotOptions: {
        pie: {
          donut: {
            size: "40%",
            labels: {
              show: true,
              name: { show: true, fontSize: "16px", fontWeight: 600 },
              value: {
                show: true,
                fontSize: "18px",
                fontWeight: 700,
                formatter: (val: any) => {
                  // val may be string, convert to number safely
                  const num = Number(val) || 0;
                  return Math.round(num).toLocaleString();
                },
              },
              total: {
                show: true,
                label: "Total Amount ",
                fontSize: "14px",
                fontWeight: "bold",
                color: "#221F20",
                formatter: () =>
                  total > 0 ? Math.round(total).toLocaleString() : "0",
              },
            },
          },
        },
      },
      tooltip: {
        enabled: true,
        y: {
          formatter: (val: number, { seriesIndex }) => {
            const labels = ["Offertory", "Project Fund", "Tithe", "Donations"];
            return `${labels[seriesIndex]}: KES ${new Intl.NumberFormat().format(val)}`;
          },
        },
      },
      dataLabels: { enabled: false },
      legend: { show: false },
    };

    this.cdr.detectChanges();
  }
}
